// /press — destination for the QR code on the printed sell sheet.
// Audience: event programmers, corporate buyers, brand-activation folks
// who saw the one-sheet and want to go deeper before reaching out.
// Voice: warm + hospitality-flavored, never corporate. Same visual
// language as the marketing home so this reads as a polished extension
// of the site, not a bolted-on PDF dump.
//
// Section flow, top to bottom:
//   1. Hero            — pitch + sizzle reel (vertical 9:16)
//   2. About           — one-liner + short bio + inline long-bio expander
//                        + Dan+Wella portrait
//   3. In-context banner — full-bleed booth-wide shot bridging bio → assets
//   4. Downloads       — sell sheet PDF + full media-kit zip
//   5. Selected Clients — logo wall (shared data with TheProof on /)
//                         preceded by the double-gasp reaction shot to
//                         bridge "credits on top" → "reaction on the ground"
//   6. Photo gallery   — filterable grid + lightbox
//   7. Other Offerings — commissions, branded content, kits, kandi, merch
//   8. Tech Rider      — power, footprint, water, display
//   9. Testimonials    — renders a zero-state when empty
//  10. Press contact   — email, socials, business basics
//
// Routing aliases /media + /presskit redirect to here in App.js so the QR
// stays canonical.

import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import FairyDustField from "./components/FairyDustField";
import PanBar from "./components/PanBar";
import { PRESS_LOGOS } from "./data/pressLogos";
import { PRESS_TESTIMONIALS } from "./data/pressTestimonials";
import { findSlot } from "./data/pressPhotos";
import BioSection from "./press/BioSection";
import OtherOfferings from "./press/OtherOfferings";
import TechRider from "./press/TechRider";
import PhotoGallery from "./press/PhotoGallery";

// Match the marketing-home hero so the page lands feeling like the same site.
const HAPPY_BLUE_BG = `
  radial-gradient(ellipse 140% 95% at 50% 35%,
    #1f59db 0%,
    #1a4b9e 30%,
    #0e2e46 70%,
    #081a2a 100%)
`;

const TILE = { bg: "#0a1c30", border: "rgba(255,218,173,0.18)" };

// Press-kit social handles. Same destinations as the footer but presented
// with explicit @-handles rather than just labels — press folks tend to
// want the handle to cite directly.
// Three socials so the chip row rotates cleanly through the pan-flag colors
// (pink / gold / sky) — no duplicated tint.
const SOCIALS = [
  { label: "Instagram", handle: "@thejoyofpancakes",  href: "https://instagram.com/thejoyofpancakes",  tint: { c: "var(--jop-pink)",           bd: "rgba(239,83,126,0.5)", bg: "rgba(239,83,126,0.10)" } },
  { label: "YouTube",   handle: "@thejoyofpancakes",  href: "https://youtube.com/@thejoyofpancakes",   tint: { c: "var(--jop-gold)",           bd: "rgba(252,179,31,0.5)", bg: "rgba(252,179,31,0.10)" } },
  { label: "Twitch",    handle: "thejoyofpancakes",   href: "https://twitch.tv/thejoyofpancakes",      tint: { c: "var(--jop-highlight-blue)", bd: "rgba(74,164,225,0.5)", bg: "rgba(74,164,225,0.10)" } },
];

export default function PressKit() {
  return (
    <div style={{ background: "#0e2e46", minHeight: "100vh" }}>
      <SiteNav />
      <PressHero />
      <BioSection />
      <InContextBanner />
      <DownloadsSection />
      <ClientsSection />
      <PhotoGallery />
      <OtherOfferings />
      <TechRider />
      <TestimonialsSection />
      <ContactSection />
      <SiteFooter />
    </div>
  );
}

// ── In-context banner ──────────────────────────────────────────────────────
// Booth-wide shot bridging the bio (who) into the materials (what). The
// hero-anchor photo is portrait-oriented (top: Dan at the griddle; middle:
// Wella at Poffridge's Bar; bottom: a guest watching), so we display it
// contained rather than full-bleed-cropped — both work surfaces have to
// stay visible or the photo loses its point.

function InContextBanner() {
  const photo = findSlot("hero-anchor");
  if (!photo) return null;
  return (
    <section
      aria-label="The Joy of Pancakes booth in context"
      style={{
        background: "#08182a",
        padding: "3rem 1.25rem 3.5rem",
        borderTop: "1px solid rgba(252,179,31,0.08)",
        borderBottom: "1px solid rgba(252,179,31,0.08)",
      }}
    >
      <figure style={{
        margin: "0 auto",
        maxWidth: "440px",
        textAlign: "center",
      }}>
        <img
          src={`/${photo.variants[1200]}`}
          srcSet={`/${photo.variants[600]} 600w, /${photo.variants[1200]} 1200w, /${photo.variants[2400]} 2400w`}
          sizes="(max-width: 520px) 88vw, 440px"
          alt={photo.alt}
          loading="lazy"
          decoding="async"
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            borderRadius: "14px",
            border: "1px solid rgba(252,179,31,0.18)",
            boxShadow: "0 16px 44px rgba(0,0,0,0.5), 0 0 0 1px rgba(252,179,31,0.08)",
          }}
        />
        <figcaption style={{
          marginTop: "0.9rem",
          color: "rgba(255,218,173,0.7)",
          fontFamily: "var(--jop-display)",
          fontSize: "0.86rem",
          letterSpacing: "0.03em",
          lineHeight: 1.5,
        }}>
          The booth in full operation — Dan at the griddle, Wella at Poffridge's Bar.
        </figcaption>
      </figure>
    </section>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────────
// Tagline + sizzle reel. The reel is 9:16 vertical, so it's framed as a
// phone-shaped column rather than stretched across the page. Autoplays
// muted on load, controls visible so press folks can rewind / unmute.

function PressHero() {
  return (
    <section
      id="press-hero"
      style={{
        position: "relative",
        background: HAPPY_BLUE_BG,
        padding: "7rem 1.25rem 4rem",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <FairyDustField density={50} seed={23} riseDistance={260} />

      <div style={{
        position: "relative",
        zIndex: 2,
        maxWidth: "1100px",
        margin: "0 auto",
        textAlign: "center",
      }}>
        <div style={{
          display: "inline-block",
          fontFamily: "var(--jop-display)",
          fontSize: "0.78rem",
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: "var(--jop-gold)",
          textTransform: "uppercase",
          padding: "0.35rem 0.85rem",
          border: "1px solid rgba(252,179,31,0.4)",
          borderRadius: "999px",
          background: "rgba(252,179,31,0.07)",
          marginBottom: "1.4rem",
        }}>
          Press &amp; Programming Kit
        </div>

        <h1 style={{
          margin: "0 0 1rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(2.2rem, 6vw, 3.8rem)",
          fontWeight: 700,
          lineHeight: 1.05,
          color: "var(--jop-gold)",
          textShadow: "0 3px 0 rgba(0,0,0,0.15), 0 4px 28px rgba(252,179,31,0.35)",
          letterSpacing: "-0.01em",
        }}>
          Ninety seconds. <br />Then you'll get it.
        </h1>

        <PanBar width={120} height={4} margin="0.4rem auto 1.6rem" />

        <p style={{
          margin: "0 auto 2.6rem",
          maxWidth: "640px",
          fontSize: "clamp(1rem, 2vw, 1.18rem)",
          lineHeight: 1.65,
          color: "var(--jop-cream)",
          fontFamily: "var(--jop-display)",
          opacity: 0.95,
          textShadow: "0 1px 6px rgba(0,0,0,0.35)",
        }}>
          We invented live pancake art in 2009. The flip is the magic trick —
          the audience can't read what's coming until the pancake comes off the
          griddle and the room loses its mind. Everything else on this page is
          here if you want to go deeper.
        </p>

        {/* Phone-shaped frame — 9:16 vertical reel centered with a soft
            golden halo so it reads as the focal point. Max-width keeps it
            sane on desktop; full-width with cap on mobile. */}
        <div style={{
          position: "relative",
          width: "min(360px, 88vw)",
          margin: "0 auto",
          aspectRatio: "9 / 16",
          borderRadius: "22px",
          overflow: "hidden",
          background: "#000",
          boxShadow: `
            0 0 0 1px rgba(252,179,31,0.35),
            0 0 60px rgba(252,179,31,0.18),
            0 30px 80px rgba(0,0,0,0.55)
          `,
        }}>
          <video
            src="/sizzle-reel.mp4"
            poster="/sizzle-reel-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            controls
            preload="metadata"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              background: "#000",
            }}
          />
        </div>

        <div style={{
          marginTop: "1.2rem",
          fontSize: "0.82rem",
          fontFamily: "var(--jop-display)",
          color: "rgba(255,218,173,0.65)",
          letterSpacing: "0.05em",
        }}>
          Tap the player to unmute · 1 min 16 sec
        </div>

        {/* Music attribution — required by YouTube Audio Library license.
            Treated as standard credits-line styling: small, low-contrast,
            but always present beneath the reel so the license condition is
            satisfied on every page view. */}
        <div style={{
          marginTop: "0.7rem",
          fontSize: "0.72rem",
          fontFamily: "var(--jop-display)",
          color: "rgba(255,218,173,0.45)",
          letterSpacing: "0.02em",
          lineHeight: 1.55,
        }}>
          Music: <em style={{ fontStyle: "italic" }}>Banana</em> by{" "}
          <a
            href="https://links.al/rmY"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "rgba(255,218,173,0.7)", textDecoration: "underline", textDecorationColor: "rgba(255,218,173,0.3)" }}
          >
            Next Route
          </a>
          {" · "}
          <a
            href="https://license.declandp.info/NR"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "rgba(255,218,173,0.55)", textDecoration: "none" }}
          >
            License
          </a>
          {" · Promoted by "}
          <a
            href="https://links.al/youtube"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "rgba(255,218,173,0.55)", textDecoration: "none" }}
          >
            YouTube Audio Library
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Downloads ───────────────────────────────────────────────────────────────
// Two stacked offers: the one-sheet PDF (fast, forwardable) and the full
// media-kit zip (everything — bios, photos, logos, tech rider). The PNG
// preview anchors the section visually and links to the PDF directly.

function DownloadsSection() {
  return (
    <section
      id="downloads"
      style={{
        background: "#0a223b",
        padding: "5rem 1.25rem",
        borderTop: "1px solid rgba(252,179,31,0.12)",
        borderBottom: "1px solid rgba(252,179,31,0.12)",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "2.4rem" }}>
          <PanBar width={72} height={3} margin="0 auto 1.1rem" />
          <h2 style={{
            margin: "0 0 0.6rem",
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(1.8rem, 4.5vw, 2.6rem)",
            color: "var(--jop-gold)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}>
            Take it with you.
          </h2>
          <p style={{
            margin: "0 auto",
            maxWidth: "640px",
            fontSize: "1rem",
            lineHeight: 1.65,
            color: "var(--jop-cream)",
            fontFamily: "var(--jop-display)",
            opacity: 0.85,
          }}>
            Forward the one-sheet to a producer in seconds. Grab the full
            media kit when you need bios, hi-res photos, logos, and the tech
            rider in one zip.
          </p>
        </div>

        <div className="jop-downloads-grid">
          {/* PNG preview anchors the layout — clickable, opens the PDF. */}
          <a
            href="/sellsheet.pdf"
            target="_blank"
            rel="noopener"
            aria-label="Open the one-sheet PDF"
            style={{
              display: "block",
              borderRadius: "12px",
              overflow: "hidden",
              border: `1px solid ${TILE.border}`,
              boxShadow: "0 14px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(252,179,31,0.08)",
              background: TILE.bg,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(252,179,31,0.25)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 14px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(252,179,31,0.08)";
            }}
          >
            <img
              src="/sellsheet-preview.png"
              alt="The Joy of Pancakes one-sheet — preview"
              loading="lazy"
              decoding="async"
              style={{ display: "block", width: "100%", height: "auto" }}
            />
          </a>

          {/* Two stacked CTA cards — sell sheet (primary) + media kit (deeper). */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <DownloadCard
              eyebrow="The one-sheet"
              title="Print it. Forward it. Paste it into the deck."
              body="Single-page PDF — what we do, who we've done it for, what it costs to bring us in."
              primary={{ href: "/sellsheet.pdf", download: true, label: "Download the one-sheet (PDF)" }}
              secondary={{ href: "/sellsheet-preview.png", target: "_blank", label: "Open the PNG preview ↗" }}
            />

            <DownloadCard
              eyebrow="Go deeper"
              title="The full media kit."
              body="Three bios, hi-res photos by category, brand logos, tech rider PDF, sizzle reel link, and sell sheet. One zip."
              primary={{ href: "/joy-of-pancakes-media-kit.zip", download: true, label: "Download the media kit (.zip)" }}
              variant="secondary"
            />
          </div>
        </div>

        <div style={{
          marginTop: "1.8rem",
          textAlign: "center",
          fontSize: "0.84rem",
          fontFamily: "var(--jop-display)",
          color: "rgba(255,218,173,0.55)",
          lineHeight: 1.5,
        }}>
          Need higher-res assets, raw video, or specific clips not in the kit? Email{" "}
          <a href="mailto:jump@thejoyofpancakes.com" style={{ color: "var(--jop-highlight-blue)", textDecoration: "none" }}>
            jump@thejoyofpancakes.com
          </a>
          {" "}— we'll send what you need.
        </div>
      </div>

      <style>{`
        .jop-downloads-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
          gap: 2rem;
          align-items: start;
        }
        @media (max-width: 760px) {
          .jop-downloads-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }
      `}</style>
    </section>
  );
}

// Inner download CTA card. `variant: "secondary"` swaps the gold gradient
// for an outlined treatment so the two cards don't both shout at the eye.
function DownloadCard({ eyebrow, title, body, primary, secondary, variant = "primary" }) {
  const isSecondary = variant === "secondary";
  return (
    <div style={{
      background: isSecondary ? "rgba(74,164,225,0.06)" : "rgba(252,179,31,0.05)",
      border: `1px solid ${isSecondary ? "rgba(74,164,225,0.3)" : "rgba(252,179,31,0.3)"}`,
      borderRadius: "14px",
      padding: "1.3rem 1.4rem 1.4rem",
      fontFamily: "var(--jop-display)",
      boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
    }}>
      <div style={{
        fontSize: "0.76rem",
        fontWeight: 800,
        letterSpacing: "0.16em",
        color: isSecondary ? "var(--jop-highlight-blue)" : "rgba(255,218,173,0.65)",
        textTransform: "uppercase",
        marginBottom: "0.45rem",
      }}>
        {eyebrow}
      </div>
      <h3 style={{
        margin: "0 0 0.55rem",
        fontFamily: "var(--jop-script)",
        fontSize: "clamp(1.3rem, 3vw, 1.7rem)",
        color: "var(--jop-cream)",
        fontWeight: 700,
        lineHeight: 1.15,
      }}>
        {title}
      </h3>
      <p style={{
        margin: "0 0 1rem",
        fontSize: "0.94rem",
        lineHeight: 1.6,
        color: "var(--jop-cream)",
        opacity: 0.9,
      }}>
        {body}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem", maxWidth: "360px" }}>
        <a
          href={primary.href}
          {...(primary.download ? { download: true } : {})}
          {...(primary.target ? { target: primary.target, rel: "noopener" } : {})}
          style={isSecondary ? {
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
            background: "rgba(74,164,225,0.12)",
            color: "var(--jop-highlight-blue)",
            textDecoration: "none",
            padding: "0.85rem 1.2rem",
            borderRadius: "12px",
            fontWeight: 800,
            fontSize: "0.95rem",
            letterSpacing: "0.03em",
            border: "1px solid rgba(74,164,225,0.5)",
            transition: "background 0.15s ease, transform 0.15s ease",
          } : {
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
            background: "linear-gradient(180deg, #ffd154 0%, var(--jop-gold) 55%, #e69600 100%)",
            color: "#1a0a12",
            textDecoration: "none",
            padding: "0.9rem 1.25rem",
            borderRadius: "12px",
            fontWeight: 900,
            fontSize: "0.96rem",
            letterSpacing: "0.04em",
            boxShadow: "0 6px 20px rgba(252,179,31,0.4), 0 0 0 1px rgba(255,255,255,0.18) inset",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <span style={{ fontSize: "1.05rem" }}>↓</span> {primary.label}
        </a>
        {secondary && (
          <a
            href={secondary.href}
            target={secondary.target || "_blank"}
            rel="noopener"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
              color: "var(--jop-highlight-blue)",
              textDecoration: "none",
              padding: "0.5rem 0.85rem",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "0.82rem",
              border: "1px solid rgba(74,164,225,0.3)",
              background: "rgba(74,164,225,0.04)",
            }}
          >
            {secondary.label}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Selected Clients ────────────────────────────────────────────────────────
// Wall of receipts. Pulls from shared data so /press and / stay in sync.
// Linked tiles open the appearance / clip in a new tab.
//
// A guest-reaction shot anchors the top of the section — the visual bridge
// between "credits on top" (logos) and "reaction on the ground" (real
// human delight). Pulled from the photo manifest's `clients-anchor` slot.

function ClientsSection() {
  const anchor = findSlot("clients-anchor");
  return (
    <section
      id="clients"
      style={{
        background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(252,179,31,0.10) 0%, transparent 60%), #133862",
        padding: "5rem 1.25rem",
      }}
    >
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        {anchor && (
          <figure style={{
            margin: "0 auto 2.6rem",
            maxWidth: "640px",
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid rgba(252,179,31,0.18)",
            boxShadow: "0 14px 38px rgba(0,0,0,0.45), 0 0 0 1px rgba(252,179,31,0.08)",
          }}>
            <img
              src={`/${anchor.variants[1200]}`}
              srcSet={`/${anchor.variants[600]} 600w, /${anchor.variants[1200]} 1200w, /${anchor.variants[2400]} 2400w`}
              sizes="(max-width: 760px) 100vw, 640px"
              alt={anchor.alt}
              loading="lazy"
              decoding="async"
              style={{ display: "block", width: "100%", height: "auto" }}
            />
            <figcaption style={{
              padding: "0.8rem 1rem",
              background: "rgba(5,9,18,0.6)",
              color: "rgba(255,218,173,0.75)",
              fontFamily: "var(--jop-display)",
              fontSize: "0.84rem",
              textAlign: "center",
              fontStyle: "italic",
            }}>
              The same reaction, every shift, in every room.
            </figcaption>
          </figure>
        )}

        <PanBar width={72} height={3} margin="0 auto 1.1rem" />
        <h2 style={{
          margin: "0 0 0.6rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(1.8rem, 4.5vw, 2.6rem)",
          color: "var(--jop-gold)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          textAlign: "center",
        }}>
          Selected clients &amp; appearances
        </h2>
        <p style={{
          margin: "0 auto 2.4rem",
          maxWidth: "560px",
          fontSize: "0.96rem",
          lineHeight: 1.6,
          color: "var(--jop-cream)",
          fontFamily: "var(--jop-display)",
          opacity: 0.8,
          textAlign: "center",
        }}>
          Network TV, Disney parks &amp; cruises, gaming &amp; comics conventions,
          flagship festivals — and a stack of local-news clips we still grin about.
        </p>

        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "1.1rem",
        }}>
          {PRESS_LOGOS.map((logo) => {
            const isLink = Boolean(logo.url);
            const Tag = isLink ? "a" : "div";
            const tagProps = isLink
              ? { href: logo.url, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <Tag
                key={logo.name}
                title={isLink ? `${logo.name} — open appearance` : logo.name}
                {...tagProps}
                style={{
                  position: "relative",
                  flex: "0 0 160px",
                  maxWidth: "160px",
                  aspectRatio: "2 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.8rem 1rem",
                  boxSizing: "border-box",
                  background: TILE.bg,
                  border: `1px solid ${TILE.border}`,
                  borderRadius: "10px",
                  textDecoration: "none",
                  boxShadow: "0 3px 10px rgba(0,0,0,0.28)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                  cursor: isLink ? "pointer" : "default",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.32), 0 0 0 2px rgba(239,83,126,0.45)";
                  e.currentTarget.style.borderColor = "transparent";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.28)";
                  e.currentTarget.style.borderColor = TILE.border;
                }}
              >
                <img
                  src={`/press/${logo.file}`}
                  alt={logo.name}
                  loading="lazy"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                />
              </Tag>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ────────────────────────────────────────────────────────────
// Always structurally rendered. When PRESS_TESTIMONIALS is populated, shows
// quote cards (with optional logo + photo). When empty, renders a graceful
// "still collecting praise" zero-state line — visible enough that press
// sees the section exists, quiet enough that it doesn't feel apologetic.

function TestimonialsSection() {
  const hasTestimonials = PRESS_TESTIMONIALS.length > 0;
  return (
    <section
      id="testimonials"
      style={{
        background: "#0a1c30",
        padding: "5rem 1.25rem",
        borderTop: "1px solid rgba(252,179,31,0.12)",
      }}
    >
      <div style={{ maxWidth: "880px", margin: "0 auto", textAlign: "center" }}>
        <PanBar width={72} height={3} margin="0 auto 1.1rem" />
        <h2 style={{
          margin: "0 0 0.6rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(1.8rem, 4.5vw, 2.6rem)",
          color: "var(--jop-gold)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
        }}>
          What hosts &amp; programmers say
        </h2>

        {!hasTestimonials && (
          <div style={{
            margin: "1.4rem auto 0",
            maxWidth: "520px",
            padding: "1.6rem 1.6rem 1.7rem",
            background: "rgba(255,255,255,0.025)",
            border: "1px dashed rgba(252,179,31,0.3)",
            borderRadius: "14px",
            fontFamily: "var(--jop-display)",
          }}>
            <div style={{
              fontFamily: "var(--jop-script)",
              fontSize: "1.5rem",
              color: "var(--jop-gold)",
              marginBottom: "0.4rem",
            }}>
              We're still collecting them.
            </div>
            <p style={{
              margin: 0,
              fontSize: "0.96rem",
              lineHeight: 1.65,
              color: "var(--jop-cream)",
              opacity: 0.88,
            }}>
              Quotes from event programmers, producers, hosts, and brand activations
              will live here. If we've worked together and you'd like to add yours,
              email{" "}
              <a href="mailto:jump@thejoyofpancakes.com" style={{ color: "var(--jop-highlight-blue)", textDecoration: "none" }}>
                jump@thejoyofpancakes.com
              </a>
              .
            </p>
          </div>
        )}

        {hasTestimonials && (
          <div style={{
            marginTop: "2.6rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.5rem",
            textAlign: "left",
          }}>
            {PRESS_TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                style={{
                  margin: 0,
                  padding: "1.6rem 1.5rem",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(252,179,31,0.18)",
                  borderRadius: "14px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                  position: "relative",
                }}
              >
                {/* Decorative open-quote glyph in gold script. */}
                <div aria-hidden="true" style={{
                  position: "absolute",
                  top: "0.4rem",
                  left: "0.9rem",
                  fontFamily: "var(--jop-script)",
                  fontSize: "3.4rem",
                  color: "rgba(252,179,31,0.25)",
                  lineHeight: 1,
                  pointerEvents: "none",
                }}>
                  “
                </div>

                {t.logo?.src && (
                  <img
                    src={`/${t.logo.src}`}
                    alt={t.logo.alt || ""}
                    loading="lazy"
                    style={{
                      maxHeight: "32px",
                      maxWidth: "120px",
                      marginBottom: "0.9rem",
                      objectFit: "contain",
                      display: "block",
                      filter: "brightness(1.05)",
                    }}
                  />
                )}

                {t.highlight && (
                  <div style={{
                    position: "relative",
                    fontFamily: "var(--jop-script)",
                    fontSize: "1.45rem",
                    lineHeight: 1.2,
                    color: "var(--jop-gold)",
                    marginBottom: "0.7rem",
                  }}>
                    {t.highlight}
                  </div>
                )}

                <blockquote style={{
                  position: "relative",
                  margin: 0,
                  fontFamily: "var(--jop-display)",
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  color: "var(--jop-cream)",
                  opacity: 0.95,
                }}>
                  {t.quote}
                </blockquote>

                <figcaption style={{
                  marginTop: "1rem",
                  paddingTop: "0.85rem",
                  borderTop: "1px solid rgba(255,218,173,0.12)",
                  fontFamily: "var(--jop-display)",
                  fontSize: "0.86rem",
                  color: "rgba(255,218,173,0.75)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.7rem",
                }}>
                  {t.photo?.src && (
                    <img
                      src={`/${t.photo.src}`}
                      alt={t.photo.alt || ""}
                      loading="lazy"
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "999px",
                        objectFit: "cover",
                        border: "1px solid rgba(252,179,31,0.3)",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: "var(--jop-cream)" }}>{t.attribution}</div>
                    {t.role && <div style={{ opacity: 0.75, fontSize: "0.82rem", marginTop: "0.15rem" }}>{t.role}</div>}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Contact ─────────────────────────────────────────────────────────────────
// Warm sign-off. Email is the primary action. Socials beneath as a row of
// pan-flag pills matching the footer's treatment.

function ContactSection() {
  return (
    <section
      id="press-contact"
      style={{
        background: `radial-gradient(ellipse 90% 60% at 50% 100%, rgba(245,118,0,0.18) 0%, rgba(239,83,126,0.08) 45%, transparent 70%), #0a223b`,
        padding: "5rem 1.25rem 4rem",
        borderTop: "1px solid rgba(252,179,31,0.12)",
      }}
    >
      <div style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center" }}>
        <PanBar width={96} height={4} margin="0 auto 1.4rem" />

        <h2 style={{
          margin: "0 0 0.6rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(2rem, 5vw, 3rem)",
          color: "var(--jop-gold)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
        }}>
          Let's make something the room can't look away from.
        </h2>

        <p style={{
          margin: "0.4rem auto 2rem",
          maxWidth: "520px",
          fontSize: "1.02rem",
          lineHeight: 1.65,
          color: "var(--jop-cream)",
          fontFamily: "var(--jop-display)",
          opacity: 0.92,
        }}>
          Activations, festivals, episodic appearances, brand partnerships —
          we love hearing what you're cooking up. The shortest path is a note:
        </p>

        <a
          href="mailto:jump@thejoyofpancakes.com?subject=Press%20%2F%20Programming%20inquiry"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.55rem",
            background: "linear-gradient(180deg, #ffd154 0%, var(--jop-gold) 55%, #e69600 100%)",
            color: "#1a0a12",
            textDecoration: "none",
            padding: "1rem 1.6rem",
            borderRadius: "999px",
            fontFamily: "var(--jop-display)",
            fontWeight: 900,
            fontSize: "1rem",
            letterSpacing: "0.04em",
            boxShadow: "0 6px 20px rgba(252,179,31,0.42), 0 0 0 1px rgba(255,255,255,0.18) inset",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 10px 28px rgba(252,179,31,0.55), 0 0 0 1px rgba(255,255,255,0.28) inset";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(252,179,31,0.42), 0 0 0 1px rgba(255,255,255,0.18) inset";
          }}
        >
          ✉  jump@thejoyofpancakes.com
        </a>

        <div style={{
          marginTop: "2.4rem",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "0.55rem",
        }}>
          {SOCIALS.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: s.tint.c,
                textDecoration: "none",
                padding: "0.4rem 0.95rem",
                border: `1px solid ${s.tint.bd}`,
                background: s.tint.bg,
                borderRadius: "999px",
                fontFamily: "var(--jop-display)",
                fontSize: "0.86rem",
                fontWeight: 700,
                letterSpacing: "0.02em",
                transition: "transform 0.15s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <strong>{s.label}</strong>{" "}
              <span style={{ opacity: 0.75, fontWeight: 500 }}>{s.handle}</span>
            </a>
          ))}
        </div>

        <div style={{
          marginTop: "2.4rem",
          fontSize: "0.84rem",
          color: "rgba(255,218,173,0.6)",
          fontFamily: "var(--jop-display)",
          lineHeight: 1.7,
        }}>
          Lucky Bean LLC · Based in St. Louis, MO<br />
          P.O. Box 63093, St. Louis, MO 63163
        </div>
      </div>
    </section>
  );
}
